import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import api from '../utils/api';

// =====================================================
// Correction du bug d'icônes Leaflet avec React/Webpack
// =====================================================
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Icône personnalisée pour les personnes
const personIcon = L.divIcon({
  className: '',
  html: `<div style="
    width: 36px; height: 36px;
    background: #2D5E4A;
    border: 3px solid #fff;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0,0,0,.25);
    display: flex; align-items: center; justify-content: center;
    font-size: 16px;
  ">👤</div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -20],
});

// Icône personnalisée pour les moments
const momentIcon = L.divIcon({
  className: '',
  html: `<div style="
    width: 36px; height: 36px;
    background: #C1440E;
    border: 3px solid #fff;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0,0,0,.25);
    display: flex; align-items: center; justify-content: center;
    font-size: 16px;
  ">✨</div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -20],
});

// Icône pour ma position
const myIcon = L.divIcon({
  className: '',
  html: `<div style="
    width: 20px; height: 20px;
    background: #2D5E4A;
    border: 3px solid #fff;
    border-radius: 50%;
    box-shadow: 0 0 0 6px rgba(45,94,74,.2);
  "></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// Composant pour recentrer la carte sur la position
function RecenterMap({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.setView([position.lat, position.lng], 14);
    }
  }, [position, map]);
  return null;
}

export default function Carte() {
  const navigate = useNavigate();
  const [position, setPosition] = useState(null);
  useEffect(() => {
  const onboardingDone = localStorage.getItem('kindeo_onboarding_done');

  if (!onboardingDone) {
    navigate('/onboarding');
  }
}, []);
  const [personnes, setPersonnes] = useState([]);
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtre, setFiltre] = useState('tout');
  const [mapReady, setMapReady] = useState(false);

  const chargerDonnees = useCallback(async (lat, lng) => {
    try {
      const [rPersonnes, rMoments] = await Promise.all([
        api.get(`/users/nearby?lat=${lat}&lng=${lng}&rayon=5`),
        api.get(`/moments/nearby?lat=${lat}&lng=${lng}&rayon=5`),
      ]);
      setPersonnes(rPersonnes.data);
      setMoments(rMoments.data);
      // Mettre à jour ma position côté serveur
      api.put('/users/me/location', { lat, lng }).catch(() => {});
    } catch {
      setError('Impossible de charger les données.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) {
      setError('Géolocalisation non supportée.');
      setLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const pos = { lat: coords.latitude, lng: coords.longitude };
        setPosition(pos);
        chargerDonnees(pos.lat, pos.lng);
      },
      () => {
        // Fallback Paris
        const pos = { lat: 48.8566, lng: 2.3522 };
        setPosition(pos);
        chargerDonnees(pos.lat, pos.lng);
      }
    );
  }, [chargerDonnees]);

  const nbPersonnes = personnes.length;
  const nbMoments = moments.length;

  return (
    <main style={{ paddingTop: 'var(--header-h)', paddingBottom: 'var(--botnav-h)' }}>

      {/* ===== CARTE LEAFLET ===== */}
      <div style={S.mapWrap}>
        {position ? (
          <MapContainer
            center={[position.lat, position.lng]}
            zoom={14}
            style={{ width: '100%', height: '100%' }}
            zoomControl={false}
            onReady={() => setMapReady(true)}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <RecenterMap position={position} />

            {/* Ma position */}
            <Marker position={[position.lat, position.lng]} icon={myIcon}>
              <Popup>
                <div style={S.popup}>
                  <strong>Vous êtes ici</strong>
                </div>
              </Popup>
            </Marker>

            {/* Personnes proches */}
            {(filtre === 'tout' || filtre === 'personnes') &&
              personnes.map(p =>
                p.lat && p.lng ? (
                  <Marker key={p.id} position={[p.lat, p.lng]} icon={personIcon}>
                    <Popup>
                      <div style={S.popup}>
                        <div style={S.popupName}>{p.prenom}</div>
                        {p.ville && <div style={S.popupSub}>{p.ville}</div>}
                        {p.bio && <div style={S.popupBio}>"{p.bio}"</div>}
                        <div style={S.popupDist}>
                          À {p.distance_km} km
                        </div>
                        <button
                          style={S.popupBtn}
                          onClick={() => navigate(`/messages/${p.id}`)}
                        >
                          Dire bonjour
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                ) : null
              )}

            {/* Moments proposés */}
            {(filtre === 'tout' || filtre === 'moments') &&
              moments.map(m =>
                m.lat && m.lng ? (
                  <Marker key={m.id} position={[m.lat, m.lng]} icon={momentIcon}>
                    <Popup>
                      <div style={S.popup}>
                        <div style={S.popupName}>{m.titre}</div>
                        {m.lieu && <div style={S.popupSub}>{m.lieu}</div>}
                        {m.description && (
                          <div style={S.popupBio}>"{m.description}"</div>
                        )}
                        <div style={S.popupDist}>
                          À {m.distance_km} km · {m.auteur_prenom}
                        </div>
                        <button
                          style={S.popupBtn}
                          onClick={() => navigate(`/messages/${m.auteur_id}`)}
                        >
                          Contacter
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                ) : null
              )}
          </MapContainer>
        ) : (
          <div style={S.mapLoading}>
            <div className="k-spinner" />
            <p style={{ fontFamily: 'Manrope,sans-serif', fontSize: 13, color: 'var(--c-text-3)', marginTop: 12 }}>
              Localisation en cours…
            </p>
          </div>
        )}

        {/* Badge résumé flottant sur la carte */}
        {position && !loading && (
          <div style={S.mapBadge}>
            <span style={S.mapBadgeDot} />
            {nbPersonnes} personne{nbPersonnes > 1 ? 's' : ''} · {nbMoments} moment{nbMoments > 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* ===== FILTRES ===== */}
      <div style={S.content}>
        <div style={S.filters}>
          {['tout', 'personnes', 'moments'].map(f => (
            <button
              key={f}
              onClick={() => setFiltre(f)}
              style={{ ...S.chip, ...(filtre === f ? S.chipActive : {}) }}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {error && <div className="k-error">{error}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="k-spinner" />
          </div>
        ) : (
  <>
    {!loading && personnes.length === 0 && moments.length === 0 && (
      <div style={{
        textAlign: 'center',
        padding: '40px 20px'
      }}>
        <h2 style={{
          fontFamily: 'Manrope',
          fontSize: 22,
          fontWeight: 800,
          color: 'var(--c-primary)',
          marginBottom: 10
        }}>
          Tu es le premier ici 👀
        </h2>

        <p style={{
          color: 'var(--c-text-2)',
          fontSize: 15,
          marginBottom: 20
        }}>
          Lance une première rencontre autour de toi.
        </p>

        <button
          onClick={() => navigate('/moment')}
          style={{
            padding: '14px 22px',
            background: 'var(--c-primary)',
            color: '#fff',
            border: 'none',
            borderRadius: 30,
            fontWeight: 700,
            cursor: 'pointer'
          }}
        >
          Créer un moment
        </button>
      </div>
    )}
            {/* ===== PERSONNES ===== */}
            {(filtre === 'tout' || filtre === 'personnes') && (
              <>
                <div className="k-section-title">
                  Personnes proches
                </div>
                {personnes.length === 0 ? (
                  <p style={S.empty}>Aucune personne trouvée dans un rayon de 5 km.</p>
                ) : (
                  personnes.map(p => (
                    <div
                      key={p.id}
                      style={S.card}
                      onClick={() => navigate(`/profil/${p.id}`)}
                    >
                      <div style={S.avatar}>{p.prenom[0].toUpperCase()}</div>
                      <div style={S.cardInfo}>
                        <div style={S.cardName}>{p.prenom}</div>
                        <div style={S.cardDist}>
                          <span className="material-symbols-outlined" style={{ fontSize: 12 }}>near_me</span>
                          À {p.distance_km} km
                        </div>
                        {p.bio && <div style={S.cardBio}>"{p.bio}"</div>}
                      </div>
                      <button
                        style={S.sayHi}
                        onClick={e => { e.stopPropagation(); navigate(`/messages/${p.id}`); }}
                      >
                        Bonjour
                      </button>
                    </div>
                  ))
                )}
              </>
            )}

            {/* ===== MOMENTS ===== */}
            {(filtre === 'tout' || filtre === 'moments') && (
              <>
                <div className="k-section-title" style={{ marginTop: filtre === 'tout' ? 24 : 0 }}>
                  Moments proposés
                </div>
                {moments.length === 0 ? (
                  <p style={S.empty}>Aucun moment proposé dans un rayon de 5 km.</p>
                ) : (
                  moments.map(m => (
                    <div key={m.id} style={S.momentCard}>
                      <div style={S.momentTop}>
                        <div style={S.momentTitle}>{m.titre}</div>
                        {m.date_moment && (
                          <div style={S.momentBadge}>
                            {new Date(m.date_moment).toLocaleDateString('fr-FR', {
                              day: 'numeric', month: 'short',
                            })}
                          </div>
                        )}
                      </div>
                      <div style={S.momentMeta}>
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>near_me</span>
                        {m.distance_km} km
                        {m.lieu && ` · ${m.lieu}`}
                        {m.heure_moment && ` · ${m.heure_moment.slice(0, 5)}`}
                      </div>
                      {m.description && (
                        <div style={S.momentDesc}>"{m.description}"</div>
                      )}
                      <div style={S.momentFooter}>
                        <span style={{ fontFamily: 'Manrope,sans-serif', fontSize: 12, color: 'var(--c-text-3)' }}>
                          Proposé par {m.auteur_prenom}
                        </span>
                        <button
                          style={S.sayHi}
                          onClick={() => navigate(`/messages/${m.auteur_id}`)}
                        >
                          Contacter
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* FAB — proposer un moment */}
      <Link to="/moment" style={S.fab} title="Proposer un moment">
        <span className="material-symbols-outlined">add</span>
      </Link>
    </main>
  );
}

// ===== STYLES =====
const S = {
  mapWrap: {
    height: '45vh',
    minHeight: 240,
    position: 'relative',
    background: '#d4e8de',
  },
  mapLoading: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mapBadge: {
    position: 'absolute',
    bottom: 12,
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(255,255,255,.9)',
    backdropFilter: 'blur(8px)',
    borderRadius: 'var(--radius-pill)',
    padding: '6px 14px',
    fontFamily: 'Manrope,sans-serif',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--c-primary)',
    zIndex: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    boxShadow: 'var(--shadow-md)',
    pointerEvents: 'none',
  },
  mapBadgeDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: 'var(--c-primary)',
  },
  content: {
    padding: '20px 16px 16px',
    maxWidth: 680,
    margin: '0 auto',
  },
  filters: {
    display: 'flex',
    gap: 8,
    overflowX: 'auto',
    paddingBottom: 4,
    marginBottom: 20,
    scrollbarWidth: 'none',
  },
  chip: {
    flexShrink: 0,
    padding: '7px 16px',
    borderRadius: 'var(--radius-pill)',
    fontFamily: 'Manrope,sans-serif',
    fontSize: 13,
    fontWeight: 600,
    background: 'var(--c-surface)',
    color: 'var(--c-text-2)',
    border: '1.5px solid var(--c-border)',
    cursor: 'pointer',
  },
  chipActive: {
    background: 'var(--c-primary)',
    color: '#fff',
    borderColor: 'var(--c-primary)',
  },
  empty: {
    fontSize: 14,
    color: 'var(--c-text-3)',
    fontStyle: 'italic',
    padding: '12px 0',
    fontFamily: 'Manrope,sans-serif',
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    background: 'var(--c-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--c-border)',
    padding: '14px 16px',
    marginBottom: 10,
    cursor: 'pointer',
    boxShadow: 'var(--shadow-sm)',
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: 'var(--c-primary-lt)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontFamily: 'Manrope,sans-serif',
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--c-primary)',
  },
  cardInfo: { flex: 1, minWidth: 0 },
  cardName: {
    fontFamily: 'Manrope,sans-serif',
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--c-text)',
    marginBottom: 2,
  },
  cardDist: {
    fontFamily: 'Manrope,sans-serif',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--c-primary)',
    marginBottom: 3,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  cardBio: {
    fontSize: 13,
    color: 'var(--c-text-2)',
    fontStyle: 'italic',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sayHi: {
    padding: '8px 14px',
    background: 'var(--c-primary-lt)',
    color: 'var(--c-primary)',
    border: 'none',
    borderRadius: 'var(--radius-pill)',
    fontFamily: 'Manrope,sans-serif',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    flexShrink: 0,
  },
  momentCard: {
    background: 'var(--c-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--c-border)',
    padding: '16px 18px',
    marginBottom: 10,
    boxShadow: 'var(--shadow-sm)',
  },
  momentTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  momentTitle: {
    fontFamily: 'Manrope,sans-serif',
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--c-text)',
    flex: 1,
  },
  momentBadge: {
    padding: '4px 10px',
    borderRadius: 'var(--radius-pill)',
    background: 'var(--c-accent-lt)',
    color: 'var(--c-accent)',
    fontFamily: 'Manrope,sans-serif',
    fontSize: 11,
    fontWeight: 700,
    flexShrink: 0,
    marginLeft: 8,
  },
  momentMeta: {
    fontFamily: 'Manrope,sans-serif',
    fontSize: 12,
    color: 'var(--c-text-3)',
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  momentDesc: {
    fontSize: 13,
    color: 'var(--c-text-2)',
    fontStyle: 'italic',
    lineHeight: 1.5,
    marginBottom: 10,
  },
  momentFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  fab: {
    position: 'fixed',
    bottom: 'calc(var(--botnav-h) + 16px)',
    right: 20,
    width: 54,
    height: 54,
    borderRadius: '50%',
    background: 'var(--c-primary)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 18px rgba(45,94,74,.35)',
    zIndex: 50,
    textDecoration: 'none',
    fontSize: 28,
  },
  // Styles popup Leaflet
  popup: {
    fontFamily: 'Manrope, sans-serif',
    minWidth: 160,
    padding: 4,
  },
  popupName: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1A1C1B',
    marginBottom: 4,
  },
  popupSub: {
    fontSize: 12,
    color: '#7A8A82',
    marginBottom: 4,
  },
  popupBio: {
    fontSize: 13,
    color: '#3D4A44',
    fontStyle: 'italic',
    marginBottom: 6,
  },
  popupDist: {
    fontSize: 12,
    fontWeight: 600,
    color: '#2D5E4A',
    marginBottom: 8,
  },
  popupBtn: {
    width: '100%',
    padding: '8px',
    background: '#2D5E4A',
    color: '#fff',
    border: 'none',
    borderRadius: 20,
    fontFamily: 'Manrope, sans-serif',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
};